import { Injectable } from '@angular/core';
import { EMPTY, Observable, Subject, Subscription, merge, timer } from 'rxjs';
import { catchError, exhaustMap, finalize, map, takeUntil, timeout } from 'rxjs/operators';

/**
 * Per-tick emission from the internal poll/timeout merge. `kind` distinguishes a
 * dashboard poll response from the max-wait safety-cap firing.
 */
type PollEmission<TResponse> =
  | { kind: 'poll'; response: TResponse }
  | { kind: 'timeout' };

/**
 * Everything a caller must supply to poll a dashboard for a pragmatic-validation
 * status while the caller holds a (native Syncfusion) grid spinner. Only the parts
 * that vary between dashboards are configurable; the resilient polling mechanics
 * (exhaustMap, per-request timeout, max-wait cap, guaranteed spinner release) are
 * owned by the service.
 */
export interface PragmaticValidationPollConfig<TResponse> {
  /** Fires one dashboard request per tick (e.g. getXxxDeliveryDashboardPaginated(...)). */
  poll: () => Observable<TResponse>;
  /** Pulls the array of dashboard rows out of a poll response (shape varies per API). */
  extractRows: (response: TResponse) => any[];
  /** True for the row whose validation we are waiting on (fileName / revision match). */
  matchRow: (row: any) => boolean;
  /**
   * Snapshot of the target row's validation timestamp taken BEFORE the new run was
   * triggered. Used to tell a fresh terminal status apart from the previous run's
   * leftover terminal status when the "In process" state was never observed.
   */
  initialValidationDateMs: number;
  /**
   * Called exactly once when the row reaches a FRESH terminal status. The caller
   * rebinds its grid here and is responsible for releasing the spinner on this path
   * (e.g. via loadDeliveries(), or an explicit hide after a synchronous rebind).
   */
  onFreshTerminal: (response: TResponse) => void;
  /**
   * Guaranteed spinner release for every NON-terminal exit: the max-wait cap elapsing,
   * or the subscription being torn down (component destroy / a new validation starting).
   */
  releaseSpinner: () => void;
  /** Optional side effect (e.g. a toast) fired ONLY when the max-wait cap elapses. */
  onTimeout?: () => void;
  /** Override the default terminal-status test (>= 3 => Complete/Error). */
  getStatusCode?: (row: any) => number | undefined;
  /** Override how a row's validation timestamp is read (defaults to pragmaticValidationDate). */
  getValidationDateMs?: (row: any) => number;
  /** Poll cadence; defaults above the endpoint p95 so a normal poll finishes in one interval. */
  pollIntervalMs?: number;
  /** Per-request cap so a single hung poll can't stall the exhaustMap loop. */
  requestTimeoutMs?: number;
  /** Absolute safety cap so the spinner can never be held forever. */
  maxWaitMs?: number;
}

/**
 * Shared pragmatic-validation status poller used by the LL/LCR/ER/BNCC dashboards.
 *
 * The Validate endpoint returns 200 immediately while the backend keeps processing,
 * so the dashboards hold a native grid spinner and poll the corresponding deliveries
 * dashboard until the selected row reaches a terminal status (Complete/Error).
 *
 * The pipeline is intentionally resilient to slow environments (QAT):
 *  - exhaustMap (not switchMap): a slow poll is allowed to finish instead of being
 *    canceled by the next tick, so `next` actually fires and the spinner releases.
 *  - inner timeout + catchError(EMPTY): one hung request can't wedge the loop.
 *  - takeUntil(timer(maxWait)): the safety cap is INDEPENDENT of `next`.
 *  - finalize(...): the held spinner is released on every non-terminal exit path.
 */
@Injectable({ providedIn: 'root' })
export class PragmaticValidationPollService {
  // Kept above the deliveries endpoints' observed p95 latency (~5s in QAT) so a normal
  // poll comfortably completes within a single interval and is not queued.
  private readonly DEFAULT_POLL_INTERVAL_MS = 8000;
  // Bounds a single slow/hung poll; on overrun the request errors and is swallowed.
  private readonly DEFAULT_REQUEST_TIMEOUT_MS = 30000;
  // Absolute cap so the spinner can never spin forever if the status never settles.
  private readonly DEFAULT_MAX_WAIT_MS = 5 * 60 * 1000;

  /**
   * Starts polling and returns the Subscription. Callers keep the handle so they can
   * stop it on destroy / when a new validation starts. Terminal status and the
   * max-wait cap both complete the stream internally (releasing the spinner).
   */
  poll<TResponse>(config: PragmaticValidationPollConfig<TResponse>): Subscription {
    const pollIntervalMs = config.pollIntervalMs ?? this.DEFAULT_POLL_INTERVAL_MS;
    const requestTimeoutMs = config.requestTimeoutMs ?? this.DEFAULT_REQUEST_TIMEOUT_MS;
    const maxWaitMs = config.maxWaitMs ?? this.DEFAULT_MAX_WAIT_MS;
    const getStatusCode = config.getStatusCode ?? ((row: any) => row?.pragmaticTaskStatusCode);
    const getValidationDateMs = config.getValidationDateMs ?? ((row: any) => this.toMs(row?.pragmaticValidationDate));

    const stop$ = new Subject<void>();
    // Have we observed the non-terminal "In process" state for THIS run? Until we do,
    // a terminal status is treated as the previous run's leftover unless its validation
    // date is newer than the pre-run snapshot.
    let sawInProcess = false;
    let terminalReached = false;
    let timedOut = false;

    const poll$ = timer(0, pollIntervalMs).pipe(
      // exhaustMap: while a poll is still in flight, IGNORE the next tick instead of
      // canceling the outstanding request (the QAT hang root cause with switchMap).
      exhaustMap(() => config.poll().pipe(
        timeout(requestTimeoutMs),
        catchError(() => EMPTY)
      )),
      map((response: TResponse): PollEmission<TResponse> => ({ kind: 'poll', response }))
    );

    // Independent max-wait signal merged into the same subscription so it is torn down
    // with everything else and fires at most once.
    const timeout$ = timer(maxWaitMs).pipe(
      map((): PollEmission<TResponse> => ({ kind: 'timeout' }))
    );

    return merge(poll$, timeout$).pipe(
      takeUntil(stop$),
      finalize(() => {
        // Terminal path already rebound + released the spinner via onFreshTerminal.
        if (terminalReached) {
          return;
        }
        // Non-terminal exit: max-wait cap elapsed, or the caller unsubscribed
        // (component destroy / a new validation starting).
        if (timedOut) {
          config.onTimeout?.();
        }
        config.releaseSpinner();
      })
    ).subscribe((emission: PollEmission<TResponse>) => {
      if (emission.kind === 'timeout') {
        timedOut = true;
        stop$.next();
        stop$.complete();
        return;
      }

      const rows = config.extractRows(emission.response) ?? [];
      const row = rows.find(r => config.matchRow(r));
      const statusCode = getStatusCode(row);
      const validationDateMs = getValidationDateMs(row);

      if (statusCode === 1 || statusCode === 2) {
        sawInProcess = true;
      }

      const isFreshTerminal =
        statusCode !== undefined && statusCode !== null && statusCode >= 3 &&
        (sawInProcess || validationDateMs > config.initialValidationDateMs);

      if (isFreshTerminal) {
        terminalReached = true;
        config.onFreshTerminal(emission.response);
        stop$.next();
        stop$.complete();
      }
    });
  }

  /** Safe epoch-ms conversion for a value that may be a Date, ISO string, or null. */
  private toMs(value: any): number {
    if (!value) {
      return 0;
    }
    const ms = new Date(value).getTime();
    return isNaN(ms) ? 0 : ms;
  }
}
